import React, { useState } from "react";
import { useLanguage } from "@/App";
import { Star, Quote, Building2 } from "lucide-react";
import { motion } from "framer-motion";

const Testimonials = () => {
  const { language } = useLanguage();

  const testimonials = [
    {
      id: "1",
      name: "Sarah van den Berg",
      company: "TechStart BV",
      role: "CEO",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop",
      logo: "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100&h=50&fit=crop",
      content_en: "EG Solutions transformed our online presence completely. Our conversions increased by 150% within the first month. The AI automation they built saves us 20+ hours per week.",
      content_nl: "EG Solutions heeft onze online aanwezigheid compleet getransformeerd. Onze conversies stegen met 150% in de eerste maand. De AI automatisering bespaart ons 20+ uur per week.",
      rating: 5,
      highlight_en: "150% more conversions",
      highlight_nl: "150% meer conversies"
    },
    {
      id: "2",
      name: "Michael de Vries",
      company: "GrowthLabs",
      role: "Marketing Director",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
      logo: "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100&h=50&fit=crop",
      content_en: "Professional, fast, and incredibly creative. The dashboard they built gives us real-time insights we never had before. Best investment we made for our brand.",
      content_nl: "Professioneel, snel en ongelooflijk creatief. Het dashboard dat ze bouwden geeft ons real-time inzichten die we nooit eerder hadden. Beste investering voor ons merk.",
      rating: 5,
      highlight_en: "Real-time insights",
      highlight_nl: "Real-time inzichten"
    },
    {
      id: "3",
      name: "Emma Jansen",
      company: "Boutique Ella",
      role: "Owner",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop",
      logo: "https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=100&h=50&fit=crop",
      content_en: "They understood exactly what we needed. Our new e-commerce site is beautiful and actually sells! Revenue up 200% since launch.",
      content_nl: "Ze begrepen precies wat we nodig hadden. Onze nieuwe webshop is prachtig en verkoopt echt! Omzet 200% gestegen sinds lancering.",
      rating: 5,
      highlight_en: "200% revenue increase",
      highlight_nl: "200% omzetstijging"
    }
  ];

  const getContent = (t) => language === "nl" ? t.content_nl : t.content_en;
  const getHighlight = (t) => language === "nl" ? t.highlight_nl : t.highlight_en;

  return (
    <section
      data-testid="testimonials-section"
      className="py-32 bg-[#030303] relative overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(255,107,0,0.05),transparent_70%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block text-[#FF6B00] text-xs font-bold uppercase tracking-[0.3em] mb-4">
            {language === "nl" ? "Klantervaringen" : "Client Stories"}
          </span>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {language === "nl" ? "Wat Klanten" : "What Clients"}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6B00] to-[#FF4500]">
              {language === "nl" ? "Zeggen" : "Say"}
            </span>
          </h2>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative"
            >
              {/* Card */}
              <div className="relative bg-[#0A0A0A] border border-white/5 p-8 h-full hover:border-[#FF6B00]/30 transition-all">
                {/* Quote icon */}
                <Quote size={40} className="text-[#FF6B00]/20 mb-6" />

                {/* Rating */}
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="fill-[#FF6B00] text-[#FF6B00]" />
                  ))}
                </div>

                {/* Highlight badge */}
                <div className="inline-flex items-center gap-2 bg-[#FF6B00]/10 px-3 py-1 mb-4">
                  <span className="text-[#FF6B00] text-sm font-bold">{getHighlight(testimonial)}</span>
                </div>

                {/* Content */}
                <p className="text-zinc-300 leading-relaxed mb-8">
                  "{getContent(testimonial)}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-4 pt-6 border-t border-white/10">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#FF6B00]/30"
                  />
                  <div className="flex-grow">
                    <p className="text-white font-bold">{testimonial.name}</p>
                    <p className="text-zinc-500 text-sm">{testimonial.role}</p>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-500">
                    <Building2 size={14} />
                    <span className="text-xs font-medium">{testimonial.company}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-zinc-500 text-sm mb-6">
            {language === "nl" ? "Vertrouwd door 150+ bedrijven" : "Trusted by 150+ companies"}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-50">
            {["TechStart", "GrowthLabs", "Boutique Ella", "FinanceHub", "CreativeStudio"].map((company, i) => (
              <span key={i} className="text-white font-bold text-lg">{company}</span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
